import * as React from 'react'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { LoginPayload, LoginResponse } from '../../../api/schema-dtos/AuthDomainDtos'
import { RtkQueryHelper } from '../../../api/shared/RtkQueryHelper'
import {
  useAuthCheckAuthenticated,
  useAuthLoginMutation,
  useAuthLogoutMutation
} from '../../../api/user/authenticationApi'
import { APP_URLS } from '../../../App'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { setNotification } from '../../shared/components/ui-widgets/uiWidgetsSlice'
import {
  authReceiveToken,
  authClearToken,
  selectAuthAssetHolderId,
  selectAuthAuthenticated
} from '../authSlice'

interface AuthProviderProps {
  children?: React.ReactNode
}

interface IAuthContext {
  authenticated: boolean
  assetHolderId: number
  onLogin: (payload: LoginPayload) => Promise<void>
  onLogout: () => Promise<void>
  checkAuthenticatedOnApi: () => boolean

  loginLoading: () => boolean
  logoutLoading: () => boolean
}

const initialContext: IAuthContext = {
  authenticated: false,
  assetHolderId: 0,
  onLogin: async (): Promise<void> => { },
  onLogout: async (): Promise<void> => { },
  checkAuthenticatedOnApi: (): boolean => { return false },

  loginLoading: (): boolean => { return false },
  logoutLoading: (): boolean => { return false }
}

const AuthContext: React.Context<IAuthContext> = React.createContext(initialContext)

// TODO: any?
const AuthProvider = ({ children }: AuthProviderProps): any => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const [apiLogin, apiLoginResult] = useAuthLoginMutation()
  const [apiLogout, apiLogoutResult] = useAuthLogoutMutation()
  const [apiCheckAuthenticated] = useAuthCheckAuthenticated()

  // ================================================================================================================

  const authAuthenticated = useAppSelector(selectAuthAuthenticated)
  const authAssetHolderId = useAppSelector(selectAuthAssetHolderId)

  // ================================================================================================================

  const tokensValid = (): boolean => {
    return authAuthenticated
  }

  const assetHolderId = (): number => {
    return authAssetHolderId ?? 0
  }

  const authenticated = (): boolean => {
    return authAuthenticated
  }

  const setTokens = (login: LoginResponse): void => {
    dispatch(authReceiveToken(login))
  }

  const clearTokens = (): void => {
    dispatch(authClearToken())
  }

  // ================================================================================================================

  const handleDefaultNavigation = (): void => {
    // console.log(location.state?.from)
    const origin: string | undefined = location.state?.from
    if (origin !== undefined) {
      navigate(origin)
    } else {
      navigate(APP_URLS.dashboard)
    }
  }

  const handleLogin = async (payload: LoginPayload): Promise<void> => {
    await apiLogin(payload)
  }

  useEffect(() => {
    if (apiLoginResult.isSuccess) {
      dispatch(setNotification({ open: true, severity: 'success', message: 'Successfully logged in.' }))
      setTokens(apiLoginResult.data)
      handleDefaultNavigation()
    }
    if (apiLoginResult.isError) {
      dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(apiLoginResult.error) }))
      clearTokens()
    }
  }, [apiLoginResult.isSuccess, apiLoginResult.isError])

  // ================================================================================================================

  const handleLogout = async (): Promise<void> => {
    if (tokensValid()) {
      await apiLogout()
    } else {
      clearTokens()
      navigate(APP_URLS.auth_login)
    }
  }

  useEffect(() => {
    if (apiLogoutResult.isSuccess) {
      dispatch(setNotification({ open: true, severity: 'success', message: 'Successfully logged out.' }))
      clearTokens()
      navigate(APP_URLS.auth_login)
    }
    if (apiLogoutResult.isError) {
      dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(apiLoginResult.error) }))
      clearTokens()
      navigate(APP_URLS.auth_login)
    }
  }, [apiLogoutResult.isSuccess, apiLogoutResult.isError])

  // ================================================================================================================

  const handleCheckApiAuthenticated = (): boolean => {
    apiCheckAuthenticated()
      .unwrap()
      .then((response) => {
        if (!response.isAuthenticated) {
          console.log('Not authenticated on API - forcing logout')
          clearTokens()
          return false
        }
      })
      .catch(async () => {
        clearTokens()
        return false
      })

    return true
  }

  // ================================================================================================================

  const value: IAuthContext = {
    authenticated: authenticated(),
    assetHolderId: assetHolderId(),
    onLogin: handleLogin,
    onLogout: handleLogout,
    checkAuthenticatedOnApi: handleCheckApiAuthenticated,

    loginLoading: () => apiLoginResult.isLoading,
    logoutLoading: () => apiLogoutResult.isLoading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): IAuthContext => {
  return React.useContext(AuthContext)
}

export { AuthProvider, AuthContext }
